import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api, { formatINR } from '@/lib/api';
import { CheckCircle, Package, House } from '@phosphor-icons/react';
import Invoice from '@/components/shop/Invoice';
import Recommendations from '@/components/shop/Recommendations';
import { CHECKOUT } from '@/constants/testIds';

// Subtle premium botanical watermark (soft green leaves + cream / peach blossoms).
// Pure inline SVG -> data URL so we don't need any new asset. Opacity is kept
// low via the wrapper so content always stays readable.
const WATERMARK_SVG = encodeURIComponent(`
<svg xmlns='http://www.w3.org/2000/svg' width='420' height='420' viewBox='0 0 420 420'>
  <g fill='none' stroke-linecap='round' stroke-linejoin='round'>
    <g stroke='#0F8A43' stroke-width='1.4' opacity='0.55'>
      <path d='M40 90 Q70 60 110 70 Q150 80 150 130 Q150 170 110 165 Q70 160 40 130 Z'/>
      <path d='M75 80 Q95 110 110 160'/>
      <path d='M300 50 Q340 70 350 110 Q360 150 320 165 Q280 175 270 140 Q260 105 300 50 Z'/>
      <path d='M315 70 Q310 110 295 155'/>
      <path d='M60 300 Q90 270 130 285 Q170 300 165 345 Q160 385 120 380 Q80 375 60 340 Z'/>
      <path d='M90 295 Q105 330 130 370'/>
      <path d='M330 280 Q370 295 375 335 Q380 375 340 380 Q300 385 290 350 Q280 315 330 280 Z'/>
      <path d='M345 295 Q335 335 315 370'/>
      <path d='M200 180 Q220 200 215 230'/>
      <path d='M210 200 Q205 220 195 235'/>
    </g>
    <g opacity='0.45'>
      <g transform='translate(210 110)'>
        <circle r='6' fill='#FF6B00'/>
        <g fill='#FFB48A'>
          <ellipse cx='0' cy='-16' rx='8' ry='12'/>
          <ellipse cx='16' cy='0' rx='12' ry='8'/>
          <ellipse cx='0' cy='16' rx='8' ry='12'/>
          <ellipse cx='-16' cy='0' rx='12' ry='8'/>
        </g>
      </g>
      <g transform='translate(110 240)'>
        <circle r='5' fill='#0F8A43'/>
        <g fill='#FCE7C8'>
          <ellipse cx='0' cy='-14' rx='7' ry='11'/>
          <ellipse cx='14' cy='0' rx='11' ry='7'/>
          <ellipse cx='0' cy='14' rx='7' ry='11'/>
          <ellipse cx='-14' cy='0' rx='11' ry='7'/>
        </g>
      </g>
      <g transform='translate(330 220)'>
        <circle r='5' fill='#FF6B00'/>
        <g fill='#FFD9BC'>
          <ellipse cx='0' cy='-12' rx='6' ry='10'/>
          <ellipse cx='12' cy='0' rx='10' ry='6'/>
          <ellipse cx='0' cy='12' rx='6' ry='10'/>
          <ellipse cx='-12' cy='0' rx='10' ry='6'/>
        </g>
      </g>
    </g>
  </g>
</svg>
`);

const watermarkStyle = {
  backgroundColor: '#FFF8EE',
  backgroundImage: `url("data:image/svg+xml;utf8,${WATERMARK_SVG}")`,
  backgroundRepeat: 'repeat',
  backgroundSize: '320px 320px',
};

export default function OrderSuccess() {
  const { orderNo } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    // Read the phone we saved at checkout to authorize full PII in the response
    let phone = '';
    try { phone = (JSON.parse(localStorage.getItem('zk_last_order') || 'null') || {}).phone || ''; } catch (e) { /* ignore */ }
    api.get(`/orders/track/${orderNo}`, { params: phone ? { phone } : {} }).then((r) => {
      setOrder(r.data);
      try {
        const prev = JSON.parse(localStorage.getItem('zk_last_order') || 'null') || {};
        localStorage.setItem('zk_last_order', JSON.stringify({ ...prev, order_no: r.data.order_no, total: r.data.total, placed_at: r.data.created_at }));
      } catch (e) { /* ignore */ }
    }).catch(() => {});
  }, [orderNo]);

  return (
    <div className="relative min-h-[80vh] overflow-hidden">
      {/* Premium botanical watermark — very subtle, decorative only */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={watermarkStyle}
      />
      {/* Soft cream radial wash for depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(220,252,231,0.35) 0%, rgba(255,237,213,0.18) 45%, rgba(255,255,255,0) 75%)',
        }}
      />

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-20 text-center fade-in">
        <div className="w-24 h-24 sm:w-28 sm:h-28 mx-auto rounded-full bg-gradient-to-br from-fresh-100 to-fresh-200 grid place-items-center shadow-lg shadow-fresh-200/50 ring-4 ring-white">
          <CheckCircle size={64} weight="duotone" className="text-fresh-700" />
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-stone-900 mt-7 sm:mt-8 leading-tight">
          Order placed!
        </h1>
        <p className="text-stone-700 mt-4 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
          Aapka order successfully place ho gaya hai. We will reach out shortly to confirm delivery.
        </p>

        <div className="mt-8 inline-flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 bg-white/85 backdrop-blur-sm border border-stone-200 px-6 sm:px-7 py-4 rounded-3xl sm:rounded-full shadow-sm">
          <span className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Order No</span>
          <span
            data-testid={CHECKOUT.successOrderNo}
            className="font-heading text-2xl sm:text-3xl text-stone-900 tracking-tight"
          >
            {orderNo}
          </span>
        </div>

        {order && (
          <div className="mt-10 text-left bg-white/95 backdrop-blur-sm border border-stone-200 rounded-3xl p-6 sm:p-8 space-y-5 shadow-xl shadow-stone-200/60">
            <div className="grid grid-cols-2 gap-y-2 text-base">
              <span className="text-stone-500">Subtotal</span>
              <span className="text-right font-medium">{formatINR(order.subtotal)}</span>
              <span className="text-stone-500">Delivery</span>
              <span className="text-right font-medium">{order.delivery_charge === 0 ? 'FREE' : formatINR(order.delivery_charge)}</span>
              {(order.platform_fee || 0) > 0 && (
                <>
                  <span className="text-stone-500">Platform fee</span>
                  <span className="text-right font-medium" data-testid="success-platform-fee">{formatINR(order.platform_fee)}</span>
                </>
              )}
            </div>
            <div className="border-t border-stone-100 pt-5 flex justify-between items-baseline gap-3">
              <span className="text-xs sm:text-sm text-stone-500 uppercase tracking-[0.15em] font-semibold">Total · COD</span>
              <span className="font-heading text-3xl sm:text-4xl text-fresh-700 tracking-tight">
                {formatINR(order.total)}
              </span>
            </div>
            <p className="text-sm sm:text-base text-stone-600">
              Delivery to {order.address?.name}, {order.address?.city} - {order.address?.pincode}
            </p>
            <div className="border-t border-stone-100 pt-5 flex justify-center">
              <Invoice order={order} variant="customer" />
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-800 transition-colors shadow-md"
          >
            <House size={18} /> Home
          </Link>
          <Link
            to={`/track?order=${orderNo}`}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-stone-300 bg-white text-stone-800 font-medium hover:bg-stone-50 transition-colors shadow-sm"
          >
            <Package size={18} /> Track order
          </Link>
        </div>

        {order && Array.isArray(order.items) && order.items.length > 0 && (
          <div className="mt-10 text-left">
            <Recommendations
              cartIds={order.items.map((it) => it.product_id).filter(Boolean)}
              title="You may also like"
              limit={4}
            />
          </div>
        )}
      </div>
    </div>
  );
}
